// src/modules/demo/context/DemoDataContext.tsx

import { createContext, useContext, useCallback, useMemo } from 'react';
import { useDemoState } from '../hooks/useDemoState';
import type { DemoState, DemoAttendanceRecord, DemoHomeworkRecord } from '../types';

export type TimelineEntry = {
  date: string;
  type: 'attendance' | 'homework' | 'observation';
  attendance?: 'P' | 'A';
  rating?: number;
  homeworkName?: string;
  homeworkStatus?: string;
  text?: string;
  createdAt?: string;
};

type DemoDataContextValue = {
  state: DemoState;
  markAttendance(studentId: string, date: string, value: 'P' | 'A'): void;
  setRating(studentId: string, date: string, value: number): void;
  activateHomework(date: string, name: string): void;
  setHomeworkStatus(studentId: string, date: string, status: 'done' | 'undone' | 'none'): void;
  addObservation(studentId: string, date: string, text: string): void;
  resetDemo(): void;
  markToastShown(toastId: string): void;
  isToastShown(toastId: string): boolean;
  markAnalyticsEmitted(eventId: string): void;
  isAnalyticsEmitted(eventId: string): boolean;
  getStudentAttendanceForDate(date: string): DemoAttendanceRecord[];
  getHomeworkForDate(date: string): DemoHomeworkRecord | null;
  getStudentTimeline(studentId: string): TimelineEntry[];
};

const DemoDataContext = createContext<DemoDataContextValue | null>(null);

export function useDemoData(): DemoDataContextValue {
  const ctx = useContext(DemoDataContext);
  if (!ctx) throw new Error('useDemoData must be used within DemoDataProvider');
  return ctx;
}

export function DemoDataProvider({ children }: { children: React.ReactNode }) {
  const { state, updateState, resetState } = useDemoState();

  const markAttendance = useCallback((studentId: string, date: string, value: 'P' | 'A') => {
    updateState((prev) => {
      const existing = prev.attendanceRecords.find(
        (r) => r.studentId === studentId && r.date === date
      );
      if (existing) {
        return {
          ...prev,
          attendanceRecords: prev.attendanceRecords.map((r) =>
            r.studentId === studentId && r.date === date
              ? { ...r, attendance: value, rating: value === 'A' ? 0 : r.rating }
              : r
          ),
        };
      }
      return {
        ...prev,
        attendanceRecords: [
          ...prev.attendanceRecords,
          {
            id: `demo-att-user-${Date.now()}`,
            studentId,
            date,
            attendance: value,
            rating: 0,
          },
        ],
      };
    });
  }, [updateState]);

  const setRating = useCallback((studentId: string, date: string, value: number) => {
    updateState((prev) => {
      const existing = prev.attendanceRecords.find(
        (r) => r.studentId === studentId && r.date === date
      );
      if (existing) {
        return {
          ...prev,
          attendanceRecords: prev.attendanceRecords.map((r) =>
            r.studentId === studentId && r.date === date
              ? { ...r, rating: value, attendance: 'P' as const }
              : r
          ),
        };
      }
      return {
        ...prev,
        attendanceRecords: [
          ...prev.attendanceRecords,
          {
            id: `demo-att-user-${Date.now()}`,
            studentId,
            date,
            attendance: 'P' as const,
            rating: value,
          },
        ],
      };
    });
  }, [updateState]);

  const activateHomework = useCallback((date: string, name: string) => {
    updateState((prev) => {
      const existing = prev.homeworkRecords.find((r) => r.date === date);
      if (existing) {
        return {
          ...prev,
          homeworkRecords: prev.homeworkRecords.map((r) =>
            r.date === date ? { ...r, name } : r
          ),
        };
      }
      return {
        ...prev,
        homeworkRecords: [
          ...prev.homeworkRecords,
          {
            id: `demo-hw-user-${Date.now()}`,
            date,
            name,
            statuses: prev.students.map((s) => ({ studentId: s.id, status: null })),
          },
        ],
      };
    });
  }, [updateState]);

  const setHomeworkStatus = useCallback(
    (studentId: string, date: string, status: 'done' | 'undone' | 'none') => {
      updateState((prev) => ({
        ...prev,
        homeworkRecords: prev.homeworkRecords.map((r) =>
          r.date === date
            ? {
                ...r,
                statuses: r.statuses.map((s) =>
                  s.studentId === studentId ? { ...s, status } : s
                ),
              }
            : r
        ),
      }));
    },
    [updateState]
  );

  const addObservation = useCallback((studentId: string, date: string, text: string) => {
    updateState((prev) => ({
      ...prev,
      observations: [
        ...prev.observations,
        {
          id: `demo-obs-user-${Date.now()}`,
          studentId,
          date,
          text,
          createdAt: new Date().toISOString(),
        },
      ],
    }));
  }, [updateState]);

  const markToastShown = useCallback((toastId: string) => {
    updateState((prev) => ({
      ...prev,
      toastsShown: prev.toastsShown.includes(toastId)
        ? prev.toastsShown
        : [...prev.toastsShown, toastId],
    }));
  }, [updateState]);

  const isToastShown = useCallback(
    (toastId: string) => state.toastsShown.includes(toastId),
    [state.toastsShown]
  );

  const markAnalyticsEmitted = useCallback((eventId: string) => {
    updateState((prev) => ({
      ...prev,
      analyticsEmitted: prev.analyticsEmitted.includes(eventId)
        ? prev.analyticsEmitted
        : [...prev.analyticsEmitted, eventId],
    }));
  }, [updateState]);

  const isAnalyticsEmitted = useCallback(
    (eventId: string) => state.analyticsEmitted.includes(eventId),
    [state.analyticsEmitted]
  );

  const getStudentAttendanceForDate = useCallback(
    (date: string) => state.attendanceRecords.filter((r) => r.date === date),
    [state.attendanceRecords]
  );

  const getHomeworkForDate = useCallback(
    (date: string) => state.homeworkRecords.find((r) => r.date === date) || null,
    [state.homeworkRecords]
  );

  const getStudentTimeline = useCallback(
    (studentId: string): TimelineEntry[] => {
      const entries: TimelineEntry[] = [];

      for (const record of state.attendanceRecords) {
        if (record.studentId === studentId) {
          entries.push({
            date: record.date,
            type: 'attendance',
            attendance: record.attendance,
            rating: record.rating,
          });
        }
      }

      for (const hw of state.homeworkRecords) {
        const studentStatus = hw.statuses.find((s) => s.studentId === studentId);
        if (studentStatus?.status) {
          entries.push({
            date: hw.date,
            type: 'homework',
            homeworkName: hw.name,
            homeworkStatus: studentStatus.status,
          });
        }
      }

      for (const obs of state.observations) {
        if (obs.studentId === studentId) {
          entries.push({
            date: obs.date,
            type: 'observation',
            text: obs.text,
            createdAt: obs.createdAt,
          });
        }
      }

      entries.sort((a, b) => b.date.localeCompare(a.date));

      return entries;
    },
    [state.attendanceRecords, state.homeworkRecords, state.observations]
  );

  const value = useMemo<DemoDataContextValue>(
    () => ({
      state,
      markAttendance,
      setRating,
      activateHomework,
      setHomeworkStatus,
      addObservation,
      resetDemo: resetState,
      markToastShown,
      isToastShown,
      markAnalyticsEmitted,
      isAnalyticsEmitted,
      getStudentAttendanceForDate,
      getHomeworkForDate,
      getStudentTimeline,
    }),
    [
      state,
      markAttendance,
      setRating,
      activateHomework,
      setHomeworkStatus,
      addObservation,
      resetState,
      markToastShown,
      isToastShown,
      markAnalyticsEmitted,
      isAnalyticsEmitted,
      getStudentAttendanceForDate,
      getHomeworkForDate,
      getStudentTimeline,
    ]
  );

  return (
    <DemoDataContext.Provider value={value}>
      {children}
    </DemoDataContext.Provider>
  );
}
